import React, { useMemo } from 'react';

import { graphql, useFragment } from 'react-relay';

import { fg } from '@atlaskit/platform-feature-flags';
import { getProductAndIDFromARI } from '@atlassian/search-client/ari';
import { type SearchResultType as SearchClientSearchResultType } from '@atlassian/search-client/common/types';
import { getGenericUrlWithAnalyticsQueryParams } from '@atlassian/search-common/analytics-query-params';

import { ProductKeys } from '../../../../../../common/constants/product-keys';
import type { SearchResultProps } from '../../../../../../common/types';
import { DescriptionBody } from '../../../../../../common/ui/description-body';
import { DisplayNameTooltip } from '../../../../../../common/ui/display-name-tooltip';
import { LastModified } from '../../../../../../common/ui/last-modified';
import { getAssetsEntityIcon } from '../../../../../../common/ui/product-entity-icon';
import { SearchResult } from '../../../../../../common/ui/search-result';
import { SearchResultType } from '../../../../../../common/ui/search-result-type';
import type { AdvancedSearchResultInteractedMetadata } from '../../../../../../common/utils/events/advanced-search-result-interacted';
import { enrichExcerpt } from '../../../../../../common/utils/search-result-utils';
import { getSiteMetadata } from '../../../../../../common/utils/sites';
import { useFireContextMenuButtonClicked } from '../../../../../../controllers/events/use-fire-context-menu-button-clicked';
import { useFireContextMenuCopyButtonClicked } from '../../../../../../controllers/events/use-fire-context-menu-copy-button-clicked';
import { useFireCopyLinkButtonClicked } from '../../../../../../controllers/events/use-fire-copy-link-button-clicked';
import { useFireResultClicked } from '../../../../../../controllers/events/use-fire-result-clicked';
import { useFireResultCtxMenuClicked } from '../../../../../../controllers/events/use-fire-result-context-menu-clicked';
import { useFireSummarizeInChatButtonClicked } from '../../../../../../controllers/events/use-fire-summarize-in-chat-button-clicked';

import type { searchAssetsObjectSchema_AtlassianSearchPage$key } from './__generated__/searchAssetsObjectSchema_AtlassianSearchPage.graphql';

export const SearchAssetsObjectSchema = ({
	node,
	position,
	searchSessionId,
	primaryProduct,
	primarySubProduct,
	selectedProduct,
	appMode,
	mainContentWidth,
	selectedSites,
	cloudId,
}: SearchResultProps<searchAssetsObjectSchema_AtlassianSearchPage$key>): React.JSX.Element | null => {
	const data = useFragment(
		graphql`
			fragment searchAssetsObjectSchema_AtlassianSearchPage on SearchResultAssetsObjectSchema {
				__typename
				id
				title
				description
				type
				lastModifiedDate
				objectSchemaEntity {
					name
					displayName
					links {
						web
					}
				}
			}
		`,
		node,
	);

	const metadata = useMemo(() => {
		const [, , , contentId] = getProductAndIDFromARI(data.id);
		return {
			id: data.id,
			containerId: null,
			spaceId: null,
			contentType: data.type,
			entityType: data.type,
			contentId,
			// Normalise to the canonical 1P key ('cmdb') so analytics pipelines
			// see a single identifier. The ARI resource-owner may be 'assets'
			// (assets-site deployments) or 'cmdb' (regular cloud).
			product: ProductKeys.Assets,
			...getSiteMetadata(data.id, cloudId, selectedSites),
		} satisfies AdvancedSearchResultInteractedMetadata;
	}, [data.id, data.type, cloudId, selectedSites]);

	const onResultClicked = useFireResultClicked({
		metadata,
		position,
	});
	const onResultCtxMenuClicked = useFireResultCtxMenuClicked({
		metadata,
		position,
	});
	const onCopyLinkButtonClicked = useFireCopyLinkButtonClicked('searchResultTitle');
	const onSummarizeInChatButtonClicked = useFireSummarizeInChatButtonClicked({
		location: 'searchResult',
		metadata,
		position,
	});
	const onContextMenuCopyButtonClicked = useFireContextMenuCopyButtonClicked();
	const onContextMenuButtonClicked = useFireContextMenuButtonClicked();
	const url = useMemo(() => {
		const webUrl = data.objectSchemaEntity?.links?.web;
		if (!webUrl) {
			return null;
		}
		return getGenericUrlWithAnalyticsQueryParams({
			url: webUrl,
			searchSessionId,
		});
	}, [data.objectSchemaEntity?.links?.web, searchSessionId]);

	const getLastModified = useMemo(() => {
		if (!data.lastModifiedDate) {
			return null;
		}
		return <LastModified lastModified={data.lastModifiedDate} />;
	}, [data.lastModifiedDate]);

	const headerMetadataParts = [
		['type', <SearchResultType type={data.type as SearchClientSearchResultType} />],
		['lastModified', getLastModified],
	];

	const schemaDisplayName = data.objectSchemaEntity?.displayName;
	const schemaInternalName = data.objectSchemaEntity?.name;

	const resolvedTitle = fg('cdm_ga') ? schemaDisplayName || '' : data.title;

	const enrichedTitle = useMemo(() => {
		return enrichExcerpt(resolvedTitle);
	}, [resolvedTitle]);

	const titleWithTooltip = fg('cdm_ga') ? (
		<DisplayNameTooltip displayName={schemaDisplayName || ''} name={schemaInternalName || ''}>
			<span>{enrichedTitle}</span>
		</DisplayNameTooltip>
	) : undefined;

	const enrichedDescription = useMemo(() => {
		return enrichExcerpt(data.description);
	}, [data.description]);

	if (!url) {
		return null;
	}

	return (
		<SearchResult
			{...data}
			product={ProductKeys.Assets}
			productSelected={!!selectedProduct}
			primaryProduct={primaryProduct}
			primarySubProduct={primarySubProduct}
			onResultClicked={onResultClicked}
			onResultContextMenu={onResultCtxMenuClicked}
			onCopyLinkButtonClicked={onCopyLinkButtonClicked}
			onSummarizeInChatButtonClicked={onSummarizeInChatButtonClicked}
			onContextMenuCopyButtonClicked={onContextMenuCopyButtonClicked}
			onContextMenuButtonClicked={onContextMenuButtonClicked}
			url={url}
			icon={getAssetsEntityIcon({
				iconUrl: null,
				type: data.type,
				size: 'medium',
			})}
			headerMetadataParts={headerMetadataParts}
			title={titleWithTooltip || enrichedTitle}
			description={<DescriptionBody description={enrichedDescription} />}
			position={position}
			appMode={appMode}
			mainContentWidth={mainContentWidth}
			selectedSites={selectedSites}
		/>
	);
};

export const isSearchAssetsObjectSchema = (
	node?: any,
): node is searchAssetsObjectSchema_AtlassianSearchPage$key => {
	return node?.__typename === 'SearchResultAssetsObjectSchema';
};
